import { forwardRef } from "react";
import { money, fmtDate, fileUrl } from "@/lib/api";

const Line = ({ children }) => (children ? <p>{children}</p> : null);

const PrintableDocument = forwardRef(function PrintableDocument({ doc, settings, title }, ref) {
  const sym = settings.currency_symbol || "$";
  return (
    <div ref={ref} className="doc-sheet bg-white text-stone-900 p-12 flex flex-col" data-testid="printable-document">
      <header className="flex justify-between gap-6 pb-6 border-b-4 border-amber-700">
        <div className="flex gap-4">
          {settings.logo_path && <img src={fileUrl(settings.logo_path)} alt="logo" className="h-20 w-20 object-contain" crossOrigin="use-credentials" data-testid="pdf-logo" />}
          <div className="text-xs text-stone-600 space-y-0.5">
            <p className="text-xl font-extrabold text-stone-900 tracking-tight" style={{ fontFamily: "Bricolage Grotesque" }} data-testid="pdf-company-name">{settings.name}</p>
            <Line>{settings.rfc && `RFC: ${settings.rfc}`}</Line>
            <Line>{settings.address}</Line>
            <Line>{[settings.phone, settings.email].filter(Boolean).join(" · ")}</Line>
            <Line>{settings.website}</Line>
          </div>
        </div>
        <div className="text-right shrink-0">
          <p className="text-2xl font-extrabold text-amber-800 tracking-tight" style={{ fontFamily: "Bricolage Grotesque" }}>{title}</p>
          <p className="font-mono text-sm mt-1" data-testid="pdf-folio">Folio: {doc.folio}</p>
          <p className="text-xs text-stone-600">Fecha: {fmtDate(doc.date)}</p>
          {doc.valid_until && <p className="text-xs text-stone-600">Válida hasta: {fmtDate(doc.valid_until)}</p>}
        </div>
      </header>

      <section className="mt-6 rounded-md bg-stone-50 border px-4 py-3 text-xs grid grid-cols-2 gap-1">
        <p className="col-span-2 text-[10px] uppercase tracking-widest text-stone-500 font-semibold">Cliente</p>
        <p className="font-semibold text-sm col-span-2">{doc.client_name}</p>
        {doc.client_phone && <p>Tel: {doc.client_phone}</p>}
        {doc.client_email && <p>{doc.client_email}</p>}
        {doc.client_rfc && <p>RFC: {doc.client_rfc}</p>}
        {doc.client_address && <p className="col-span-2">{doc.client_address}</p>}
      </section>

      <table className="mt-6 w-full text-xs">
        <thead>
          <tr className="bg-[#1E1B18] text-stone-100 text-left">
            <th className="px-3 py-2">Concepto</th><th className="px-3 py-2 text-right w-16">Cant.</th>
            <th className="px-3 py-2 text-right w-28">P. unitario</th><th className="px-3 py-2 text-right w-28">Importe</th>
          </tr>
        </thead>
        <tbody>
          {doc.items.map((it, i) => (
            <tr key={i} className="border-b">
              <td className="px-3 py-2">{it.description}</td>
              <td className="px-3 py-2 text-right font-mono">{it.quantity}</td>
              <td className="px-3 py-2 text-right font-mono">{money(it.unit_price, sym)}</td>
              <td className="px-3 py-2 text-right font-mono">{money(it.quantity * it.unit_price, sym)}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className="mt-4 flex justify-end">
        <div className="w-64 text-xs space-y-1">
          <div className="flex justify-between"><span>Subtotal</span><span className="font-mono">{money(doc.subtotal, sym)}</span></div>
          {doc.discount > 0 && <div className="flex justify-between"><span>Descuento</span><span className="font-mono">-{money(doc.discount, sym)}</span></div>}
          <div className="flex justify-between"><span>{settings.tax_name} ({doc.tax_rate}%)</span><span className="font-mono">{money(doc.tax, sym)}</span></div>
          <div className="flex justify-between text-base font-bold border-t-2 border-amber-700 pt-1"><span>TOTAL</span><span className="font-mono" data-testid="pdf-total">{money(doc.total, sym)}</span></div>
          {doc.advance > 0 && (
            <>
              <div className="flex justify-between"><span>Anticipo</span><span className="font-mono">-{money(doc.advance, sym)}</span></div>
              <div className="flex justify-between font-semibold"><span>Saldo</span><span className="font-mono">{money(doc.balance, sym)}</span></div>
            </>
          )}
        </div>
      </div>

      {doc.notes && (
        <section className="mt-6 text-xs">
          <p className="text-[10px] uppercase tracking-widest text-stone-500 font-semibold mb-1">Notas</p>
          <p className="whitespace-pre-line">{doc.notes}</p>
        </section>
      )}

      <div className="flex-1" />
      <footer className="mt-10 text-[11px] text-stone-600 space-y-4">
        {settings.bank_info && <p className="whitespace-pre-line"><span className="font-semibold">Datos de pago: </span>{settings.bank_info}</p>}
        {settings.footer_note && <p className="whitespace-pre-line">{settings.footer_note}</p>}
        <div className="grid grid-cols-2 gap-16 pt-10">
          <p className="border-t border-stone-400 pt-1 text-center">{settings.name}</p>
          <p className="border-t border-stone-400 pt-1 text-center">Firma del cliente</p>
        </div>
      </footer>
    </div>
  );
});

export default PrintableDocument;
